import { c as _c } from "react/compiler-runtime";
import { PortableText } from "@portabletext/react";
import { Distribution, Donation, TaxUnit } from "../../../../../models";
import { Organization } from "../../../../shared/components/Widget/types/Organization";
import { CauseArea } from "../../../../shared/components/Widget/types/CauseArea";
import { onlyDate, thousandize } from "../../../../../util/formatting";
import { ErrorMessage } from "../../ErrorMessage/ErrorMessage";
import { GenericList } from "../GenericList";
import { ListRow } from "../GenericListRow";
import { DonationDetails, DonationDetailsConfiguration } from "./DonationDetails";
import { ReactNode } from "react";
export type TableFieldTypes = "string" | "sum" | "date" | "paymentmethod";
export type DonationsListConfiguration = {
  columns: {
    title: string;
    value: keyof Donation;
    type: TableFieldTypes;
    width?: string;
  }[];
  tax_deduction_current_year_template: string;
  tax_deduction_previous_year_template: string;
  no_donations_placeholder_text: any[];
};
export const DonationList: React.FC<{
  donations: Donation[];
  distributions: Map<string, Distribution>;
  taxUnits: TaxUnit[];
  year: string;
  configuration: DonationsListConfiguration;
  detailsConfiguration?: DonationDetailsConfiguration;
  firstOpen: boolean;
  organizations: Organization[];
  causeAreas: CauseArea[];
}> = t0 => {
  const $ = _c(40);
  const {
    donations,
    distributions,
    taxUnits,
    year,
    configuration,
    detailsConfiguration,
    firstOpen,
    organizations,
    causeAreas
  } = t0;
  let taxDeductionText = undefined;
  if ($[0] !== configuration.tax_deduction_current_year_template || $[1] !== configuration.tax_deduction_previous_year_template || $[2] !== taxUnits || $[3] !== year) {
    let t1;
    if ($[5] !== year) {
      t1 = (acc, unit) => {
        const deduction = unit.taxDeductions?.find(td => td.year === parseInt(year));
        if (deduction) {
          return {
            yearlyDeduction: acc.yearlyDeduction + deduction.deduction,
            yearlyBenefit: acc.yearlyBenefit + deduction.benefit
          };
        } else {
          return acc;
        }
      };
      $[5] = year;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    const taxDeductions = taxUnits.reduce(t1, {
      yearlyDeduction: 0,
      yearlyBenefit: 0
    });
    if (taxDeductions.yearlyDeduction > 0) {
      let taxDeductionTextSplit;
      let t2;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          whiteSpace: "nowrap"
        };
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      const taxDeductionSumElement = <span style={t2}>{thousandize(Math.round(taxDeductions.yearlyBenefit))}</span>;
      if (year === new Date().getFullYear().toString()) {
        taxDeductionTextSplit = configuration.tax_deduction_current_year_template.split("{{deduction}}");
      } else {
        taxDeductionTextSplit = configuration.tax_deduction_previous_year_template.split("{{deduction}}");
      }
      let t3;
      if ($[8] !== year) {
        t3 = el => el.replace("{{year}}", year);
        $[8] = year;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      taxDeductionTextSplit = taxDeductionTextSplit.map(t3);
      let t4;
      if ($[10] !== taxDeductionSumElement || $[11] !== taxDeductionTextSplit[0] || $[12] !== taxDeductionTextSplit[1]) {
        t4 = <span>{taxDeductionTextSplit[0]}{taxDeductionSumElement}{taxDeductionTextSplit[1]}</span>;
        $[10] = taxDeductionSumElement;
        $[11] = taxDeductionTextSplit[0];
        $[12] = taxDeductionTextSplit[1];
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      taxDeductionText = t4;
    }
    $[0] = configuration.tax_deduction_current_year_template;
    $[1] = configuration.tax_deduction_previous_year_template;
    $[2] = taxUnits;
    $[3] = year;
    $[4] = taxDeductionText;
  } else {
    taxDeductionText = $[4];
  }
  let t1;
  if ($[14] !== configuration.columns) {
    t1 = configuration.columns.map(_temp);
    $[14] = configuration.columns;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const headers = t1;
  let t2;
  if ($[16] !== causeAreas || $[17] !== configuration.columns || $[18] !== detailsConfiguration || $[19] !== distributions || $[20] !== donations || $[21] !== firstOpen || $[22] !== organizations) {
    let t3;
    if ($[24] !== causeAreas || $[25] !== configuration.columns || $[26] !== detailsConfiguration || $[27] !== distributions || $[28] !== firstOpen || $[29] !== organizations) {
      t3 = (donation, index) => ({
        id: donation.id.toString(),
        defaultExpanded: firstOpen && index === 0 ? true : false,
        cells: configuration.columns.map(column_0 => ({
          value: formatField(donation[column_0.value], column_0.type)
        })),
        details: detailsConfiguration ? <DonationDetails key={donation.id} donation={donation} sum={donation.sum} distribution={distributions.get(donation.KID.trim()) as Distribution} timestamp={new Date(donation.timestamp)} configuration={detailsConfiguration} organizations={organizations} causeAreas={causeAreas} /> : <ErrorMessage>Missing donation details configuration in Sanity</ErrorMessage>,
        element: donation
      });
      $[24] = causeAreas;
      $[25] = configuration.columns;
      $[26] = detailsConfiguration;
      $[27] = distributions;
      $[28] = firstOpen;
      $[29] = organizations;
      $[30] = t3;
    } else {
      t3 = $[30];
    }
    t2 = donations.map(t3);
    $[16] = causeAreas;
    $[17] = configuration.columns;
    $[18] = detailsConfiguration;
    $[19] = distributions;
    $[20] = donations;
    $[21] = firstOpen;
    $[22] = organizations;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  const rows = t2;
  let t3;
  if ($[31] !== configuration.no_donations_placeholder_text) {
    t3 = <div><PortableText value={configuration.no_donations_placeholder_text} /></div>;
    $[31] = configuration.no_donations_placeholder_text;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  const emptyPlaceholder = t3;
  let t4;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [20, 70];
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  let t5;
  if ($[34] !== emptyPlaceholder || $[35] !== headers || $[36] !== rows || $[37] !== taxDeductionText || $[38] !== year) {
    t5 = <GenericList title={year} supplementalInformation={taxDeductionText} headers={headers} rows={rows} emptyPlaceholder={emptyPlaceholder} expandable={true} proportions={t4} />;
    $[34] = emptyPlaceholder;
    $[35] = headers;
    $[36] = rows;
    $[37] = taxDeductionText;
    $[38] = year;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  return t5;
};
const formatField: (field: any, type: TableFieldTypes) => string = (field, type) => {
  switch (type) {
    case "string":
      return field;
    case "sum":
      return thousandize(Math.round(parseFloat(field))) + " kr";
    case "date":
      return onlyDate(field);
    case "paymentmethod":
      return mapPaymentMethodString(field);
    default:
      return field;
  }
};
const mapPaymentMethodString = (paymentMethod: string): string => {
  switch (paymentMethod) {
    case "Vipps Recurring":
      return "Vippsavtale";
    case "Vipps KID":
      return "Vipps";
    case "Bank u/KID":
      return "Bank";
    default:
      return paymentMethod;
  }
};
function _temp(column) {
  return {
    label: column.title,
    width: column.width + "%"
  };
}